import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react'
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser'
import { MainLayout } from '@/components/layout/MainLayout'
import { PageLoader } from '@/components/common/LoadingSpinner'
import { AdminRoute, ManagerRoute } from '@/components/common/AdminRoute'
import { Toaster } from '@/components/ui/toaster'
import { useAuthStore } from '@/store/authStore'
import { initializeMsal, loginRequest } from '@/lib/msal'
import { authApi } from '@/api/endpoints/auth'
import {
  Dashboard,
  Leads,
  Activities,
  Conversations,
  Prompts,
  KnowledgeBase,
  InboundNumbers,
  Scout,
  Users,
  Voices,
  Engagement,
  EngagementBatch,
  AgentPool,
  EmailTemplates,
} from '@/pages'
import './index.css'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

// Login screen component
function LoginScreen() {
  const { instance, inProgress } = useMsal()

  const handleLogin = () => {
    instance.loginRedirect(loginRequest)
  }

  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-primary-900 to-primary-700">
      <div className="max-w-md rounded-xl bg-white p-8 shadow-2xl text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-xl bg-primary-100">
          <span className="text-3xl font-bold text-primary-700">R</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Riviera Finance AI
        </h1>
        <p className="text-gray-500 mb-6">Sales Agent Dashboard</p>
        <p className="text-sm text-gray-600 mb-6">
          Please sign in with your Microsoft account to continue.
        </p>
        <button
          onClick={handleLogin}
          className="flex items-center justify-center gap-3 w-full rounded-lg bg-primary-600 px-6 py-3 text-white font-medium hover:bg-primary-700 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
            <rect width="10" height="10" fill="#f25022" />
            <rect x="11" width="10" height="10" fill="#7fba00" />
            <rect y="11" width="10" height="10" fill="#00a4ef" />
            <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
          </svg>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  )
}

// Protected route wrapper
function ProtectedRoutes() {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const { setUser, setLoading, authEnabled } = useAuthStore()
  const [userLoaded, setUserLoaded] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [redirectHandled, setRedirectHandled] = useState(false)

  // Handle redirect parameter after authentication
  useEffect(() => {
    if (userLoaded && isAuthenticated && !redirectHandled) {
      // Check sessionStorage first (set by msal.ts before login redirect)
      // Then fall back to URL params (in case user is already logged in)
      let redirectPath = sessionStorage.getItem('auth_redirect_path')
      if (!redirectPath) {
        const searchParams = new URLSearchParams(window.location.search)
        redirectPath = searchParams.get('redirect')
      }

      if (redirectPath) {
        // Clear the stored redirect path
        sessionStorage.removeItem('auth_redirect_path')
        setRedirectHandled(true)
        // Decode the path (it was URL-encoded)
        const decodedPath = decodeURIComponent(redirectPath)
        // Use replace to avoid adding to browser history
        navigate(decodedPath, { replace: true })
      }
    }
  }, [userLoaded, isAuthenticated, redirectHandled, navigate])

  useEffect(() => {
    async function loadUser() {
      if (isAuthenticated && accounts.length > 0) {
        try {
          const response = await authApi.getCurrentUser()
          setUser(response.data)
          setAuthError(null)
        } catch (error: unknown) {
          console.error('Failed to load user:', error)
          // Check if it's a 401 error - token might be expired
          const axiosError = error as { response?: { status?: number } }
          if (axiosError?.response?.status === 401) {
            setAuthError('Session expired. Please sign in again.')
            // Clear any stale user data
            setUser(null)
          } else {
            setAuthError('Failed to load user profile.')
          }
        }
      }
      setUserLoaded(true)
      setLoading(false)
    }

    if (inProgress === InteractionStatus.None) {
      loadUser()
    }
  }, [isAuthenticated, accounts, inProgress, setUser, setLoading])

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  if (inProgress !== InteractionStatus.None || !userLoaded) {
    return <PageLoader />
  }

  if (!isAuthenticated && authEnabled) {
    return <LoginScreen />
  }

  // Show auth error screen with logout option
  if (authError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-primary-900 to-primary-700">
        <div className="max-w-md rounded-xl bg-white p-8 shadow-2xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-xl bg-red-100">
            <span className="text-3xl">!</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Error
          </h1>
          <p className="text-gray-500 mb-6">{authError}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleLogout}
              className="rounded-lg bg-gray-200 px-6 py-3 text-gray-700 font-medium hover:bg-gray-300 transition-colors"
            >
              Sign Out
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary-600 px-6 py-3 text-white font-medium hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout onLogout={handleLogout} />}>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="activities" element={<Activities />} />
        <Route path="conversations" element={<Conversations />} />
        {/* Admin-only routes: Configuration */}
        <Route path="prompts" element={<AdminRoute><Prompts /></AdminRoute>} />
        <Route path="knowledge" element={<AdminRoute><KnowledgeBase /></AdminRoute>} />
        <Route path="inbound" element={<AdminRoute><InboundNumbers /></AdminRoute>} />
        <Route path="voices" element={<AdminRoute><Voices /></AdminRoute>} />
        <Route path="engagement" element={<AdminRoute><Engagement /></AdminRoute>} />
        <Route path="engagement-batch" element={<ManagerRoute><EngagementBatch /></ManagerRoute>} />
        <Route path="agent-pool" element={<AdminRoute><AgentPool /></AdminRoute>} />
        <Route path="scout" element={<AdminRoute><Scout /></AdminRoute>} />
        <Route path="email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
        {/* Admin-only routes: User Management */}
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

// App without MSAL (when auth is disabled)
function AppWithoutAuth() {
  const { setUser, setLoading, setAuthEnabled } = useAuthStore()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    async function init() {
      setAuthEnabled(false)
      try {
        const response = await authApi.getCurrentUser()
        setUser(response.data)
      } catch (error) {
        console.error('Failed to load user:', error)
      }
      setLoading(false)
      setReady(true)
    }
    init()
  }, [setUser, setLoading, setAuthEnabled])

  if (!ready) {
    return <PageLoader />
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="activities" element={<Activities />} />
        <Route path="conversations" element={<Conversations />} />
        {/* Admin-only routes: Configuration (but accessible when auth disabled) */}
        <Route path="prompts" element={<AdminRoute><Prompts /></AdminRoute>} />
        <Route path="knowledge" element={<AdminRoute><KnowledgeBase /></AdminRoute>} />
        <Route path="inbound" element={<AdminRoute><InboundNumbers /></AdminRoute>} />
        <Route path="voices" element={<AdminRoute><Voices /></AdminRoute>} />
        <Route path="engagement" element={<AdminRoute><Engagement /></AdminRoute>} />
        <Route path="engagement-batch" element={<ManagerRoute><EngagementBatch /></ManagerRoute>} />
        <Route path="agent-pool" element={<AdminRoute><AgentPool /></AdminRoute>} />
        <Route path="scout" element={<AdminRoute><Scout /></AdminRoute>} />
        <Route path="email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
        {/* Admin-only routes: User Management (but accessible when auth disabled) */}
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

// Main App component
function App() {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null)
  const [authEnabled, setAuthEnabled] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function init() {
      try {
        const instance = await initializeMsal()
        if (instance) {
          setMsalInstance(instance)
          setAuthEnabled(true)
        } else {
          setAuthEnabled(false)
        }
      } catch (err) {
        console.error('MSAL initialization failed:', err)
        setError('Failed to initialize authentication')
        setAuthEnabled(false)
      }
    }
    init()
  }, [])

  if (authEnabled === null) {
    return <PageLoader />
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-primary-600 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {authEnabled && msalInstance ? (
          <MsalProvider instance={msalInstance}>
            <ProtectedRoutes />
          </MsalProvider>
        ) : (
          <AppWithoutAuth />
        )}
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  )
}

export default App
